import { hasRenderableIcon } from '@/screens/storefront-microproducts/micro-product-icon-config';
import {
  isAuthenticatedUserContext,
  useMaybeUserContext,
} from '@/contexts/user-context';
import {
  MicroProduct,
  MicroProductsSlot,
  useMicroProductsLoader,
} from '@domain/microproducts/loaders/use-microproducts-loader';
import { useMemo } from 'react';
import { useSalsaManifest } from '@domain/microproducts/loaders/use-salsa-manifest';

export interface UseStorefrontMicroProductsResult {
  headerActionProducts: MicroProduct[];
  loading: boolean;
  moreMenuProducts: MicroProduct[];
  products: MicroProduct[];
  promotedProducts: MicroProduct[];
}

const getNavConfig = (product: MicroProduct): Record<string, unknown> => {
  const { nav } = product.props;
  return typeof nav === 'object' && nav !== null
    ? (nav as Record<string, unknown>)
    : {};
};

// Checks if a product is promoted, which also requires a display name and
// renderable icon. `=== true` rather than truthiness: props is free-form
// JSON, so a string like "yes" must not reshape the nav.
const isPromotedToNav = (product: MicroProduct): boolean =>
  getNavConfig(product).promoted === true &&
  hasRenderableIcon(product) &&
  Boolean(product.displayName);

// Checks if a product should render in the mobile header (which requires
// a renderable icon)
const isMobileHeaderAction = (product: MicroProduct): boolean =>
  getNavConfig(product).mobile_header_action === true &&
  hasRenderableIcon(product);

/**
 * Loads the microproducts configured for the storefront (operator portal)
 * sidebar slot, scoped to the selected restaurant.
 *
 * Restaurant context is required: Calyx resolves restaurant-membership
 * users' access via the `restaurant_id` query param and returns empty slots
 * without it (see useMicroProductsLoader).
 *
 * Zero products is the default — the GA slot config is the rollout gate —
 * so consumers must render nothing for an empty list.
 */
export const useStorefrontMicroProducts =
  (): UseStorefrontMicroProductsResult => {
    // Maybe-variant + type guard: the drawer renders in contexts where the
    // user context may not be authenticated — no restaurant, no fetch.
    const userContext = useMaybeUserContext();
    const restaurantUUID = isAuthenticatedUserContext(userContext)
      ? userContext.selectedRestaurantUUID
      : undefined;
    const { loading, slots } = useMicroProductsLoader({
      enabled: Boolean(restaurantUUID),
      restaurantUUID,
    });

    const slotProducts = useMemo(
      () =>
        slots.find((s) => s.slot === MicroProductsSlot.STOREFRONT_SIDEBAR)
          ?.products ?? [],
      [slots],
    );

    // The manifest only exists to filter out products without a deployed SPA
    // — skip the fetch entirely while the slot is empty (the default for all
    // operators until GA config exists).
    const {
      availableProducts,
      loaded: manifestLoaded,
      loading: manifestLoading,
    } = useSalsaManifest({ enabled: slotProducts.length > 0 });

    // Only filter once the manifest has loaded — until then, show all
    // products. Same semantics as the sales hub sidebar nav items loader.
    const products = useMemo(() => {
      if (!manifestLoaded) {
        return slotProducts;
      }
      return slotProducts.filter((p) => availableProducts.has(p.id));
    }, [availableProducts, manifestLoaded, slotProducts]);

    const [promotedProducts, moreMenuProducts] = useMemo(() => {
      const promoted: MicroProduct[] = [];
      const remainder: MicroProduct[] = [];
      for (const product of products) {
        if (isPromotedToNav(product)) {
          promoted.push(product);
        } else {
          remainder.push(product);
        }
      }

      return [promoted, remainder];
    }, [products]);

    const headerActionProducts = useMemo(
      () => products.filter(isMobileHeaderAction),
      [products],
    );

    return {
      headerActionProducts,
      loading: loading || manifestLoading,
      moreMenuProducts,
      products,
      promotedProducts,
    };
  };
